import type { Resource } from '@streamnative/unified-rbac/dist/proto/condition'
import {
  view,
  User,
  update,
  create,
  remove,
  ServiceAccountBinding,
  ServiceAccount,
  APIKeys,
  Subscription,
  Product,
  CustomerPortalRequest,
  PulsarCluster,
  use,
  PulsarInstance,
  adminTenant,
  Secret,
  CloudEnvironment,
  CloudConnection,
  TenantWrapper,
  PulsarClusterWrapper,
  browse,
  PulsarInstanceWrapper,
  PoolOption,
  PaymentIntent,
  Volume,
  Catalog,
  Unilink,
  KafkaCluster
} from '@/composables/useRbac'
import {
  CloudServiceaccountbindingsCreate,
  CloudServiceaccountbindingsAlter,
  CloudUsersAlter,
  CloudUsersCreate,
  CloudUsersDelete,
  CloudServiceaccountsDelete,
  CloudServiceaccountbindingsDelete,
  CloudServiceaccountsAlter,
  CloudApikeysDelete,
  CloudApikeysAlter,
  CloudApikeysCreate,
  BillingProductsDescribe,
  BillingCustomerportalrequestsCreate,
  CloudServiceaccountsCreate,
  CloudPulsarclustersDescribe,
  CloudUsersDescribe,
  CloudPulsarinstancesDescribe,
  CloudMetricsPulsarQuery,
  PulsarTenantsDescribe,
  PulsarNamespacesDescribe,
  PulsarTopicsDescribe,
  CloudServiceaccountsDescribe,
  CloudApikeysDescribe,
  CloudSecretsDescribe,
  PulsarSinksDescribe,
  PulsarSourcesDescribe,
  PulsarSinksCreate,
  PulsarSourcesCreate,
  PulsarSourcesAlter,
  PulsarSinksAlter,
  PulsarFunctionsDescribe,
  CloudPulsarclustersAlter,
  CloudCloudenvironmentsDescribe,
  CloudCloudconnectionsDescribe,
  CloudPulsarclustersCreate,
  CloudPulsarinstancesCreate,
  CloudPulsarinstancesAlter,
  CloudCloudconnectionsCreate,
  CloudCloudenvironmentsCreate,
  CloudPulsarinstancesDelete,
  CloudPulsarclustersDelete,
  PulsarTenantsCreate,
  PulsarTenantsDelete,
  type Permission,
  PulsarTenantsAlter,
  CloudSecretsCreate,
  CloudSecretsDelete,
  CloudSecretsAlter,
  CloudServiceaccountbindingsDescribe,
  PulsarNamespacesCreate,
  PulsarNamespacesAlter,
  PulsarNamespacesDelete,
  PulsarTopicsCreate,
  PulsarSourcesDelete,
  PulsarSinksDelete,
  PulsarFunctionsCreate,
  PulsarFunctionsDelete,
  PulsarFunctionsAlter,
  CloudCloudenvironmentsDelete,
  CloudCloudenvironmentsAlter,
  CloudCloudconnectionsAlter,
  CloudCloudconnectionsDelete,
  CloudPoolsDescribe,
  PulsarSubscriptionsDescribe,
  PulsarSubscriptionsCreate,
  PulsarSubscriptionsAlter,
  PulsarSubscriptionsDelete,
  PulsarTopicsDelete,
  PulsarTopicsAlter,
  PulsarMessagesProduce,
  PulsarMessagesConsume,
  PulsarBrokersDescribe,
  PulsarBrokersAlter,
  KsnSourcesDelete,
  KsnSinksDelete,
  KsnSinksAlter,
  KsnSourcesAlter,
  KsnSinksCreate,
  KsnSourcesCreate,
  KsnSinksDescribe,
  KsnSourcesDescribe,
  BillingPaymentintentsDescribe,
  BillingPaymentintentsCreate,
  BillingSubscriptionsDescribe,
  CloudSelfOrganizationsAlter,
  CloudUnilinkDescribe,
  CloudUnilinkCreate,
  CloudUnilinkAlter,
  CloudUnilinkDelete,
  CloudVolumesAlter,
  CloudVolumesCreate,
  CloudVolumesDelete,
  CloudVolumesDescribe,
  CloudCatalogsAlter,
  CloudCatalogsCreate,
  CloudCatalogsDelete,
  CloudCatalogsDescribe,
  CloudRolebindingsDescribe,
  CloudRolebindingsAlter,
  CloudRolebindingsCreate,
  CloudRolebindingsDelete,
  CloudAlertsDescribe,
  CloudAlertsCreate,
  CloudAlertsAlter,
  CloudAlertsDelete,
  SchemaSubjectsRead,
  SchemaSubjectsWrite,
  SchemaSubjectsDelete,
  SchemaCompatibilitiesRead,
  SchemaCompatibilitiesWrite,
  CloudPooloptionsDescribe,
  CloudInstancesDescribe,
  CloudInstancesAlter,
  CloudInstancesCreate,
  CloudInstancesDelete,
  CloudKafkaClustersDescribe,
  CloudKafkaClustersCreate,
  CloudKafkaClustersDelete,
  CloudKafkaClustersAlter
} from '@/composables/useRbacV2'
import { can, type RbacQueryV2 } from '@/utils/rbacHelper'
import type { RouteLocationNormalized } from 'vue-router'

interface RbacStateParams {
  debug?: boolean
  to?: RouteLocationNormalized
  organization?: string
  instance?: string
  instanceType?: 'pulsar' | 'kafka'
  cluster?: string
  tenant?: string
  namespace?: string
  topic_name?: string
  topic_domain?: string
  subscription?: string
  connectorType?: 'sink' | 'source'
  serviceAccount?: string
  apikey?: string
  secret?: string
  schema_subject?: string
  catalog?: string
}

const AuthQueryLevelMap = {
  organization: 0,
  instance: 1,
  cluster: 2,
  tenant: 3,
  namespace: 4,
  topic: 5
}

const queryV2ItemFn = (
  permission: Permission,
  param?:
    | {
        resource?: Resource
        level?: 'organization' | 'instance' | 'cluster' | 'tenant' | 'namespace' | 'topic'
      }
    | undefined
) => {
  const { organization, instance, tenant, namespace, topic } = usePulsarState()
  const { activeClusterName } = useCluster()
  const { activeTopicDetail } = useTopic()
  return {
    permission,
    resource: {
      common: {
        organization:
          param?.resource?.common?.organization ||
          (param?.level && AuthQueryLevelMap[param.level] >= AuthQueryLevelMap.organization
            ? organization.value
            : undefined),
        instance:
          param?.resource?.common?.instance ||
          (param?.level && AuthQueryLevelMap[param.level] >= AuthQueryLevelMap.instance
            ? instance.value
            : undefined),
        cluster:
          param?.resource?.common?.cluster ||
          (param?.level && AuthQueryLevelMap[param.level] >= AuthQueryLevelMap.cluster
            ? activeClusterName.value
            : undefined),
        tenant:
          param?.resource?.common?.tenant ||
          (param?.level && AuthQueryLevelMap[param.level] >= AuthQueryLevelMap.tenant
            ? tenant.value
            : undefined),
        namespace:
          param?.resource?.common?.namespace ||
          (param?.level && AuthQueryLevelMap[param.level] >= AuthQueryLevelMap.namespace
            ? namespace.value
            : undefined),
        topic:
          param?.resource?.common?.topic ||
          (param?.level && AuthQueryLevelMap[param.level] >= AuthQueryLevelMap.topic
            ? topic.value
            : undefined)
      },
      pulsar: param?.resource?.pulsar
        ? param?.resource?.pulsar
        : param?.level &&
          AuthQueryLevelMap[param.level] >= AuthQueryLevelMap.topic &&
          topic.value &&
          activeTopicDetail.value
        ? {
            topic: {
              domain: activeTopicDetail.value?.persistency ? 'persistent' : 'non-persistent'
            }
          }
        : undefined,
      cloud: param?.resource?.cloud,
      schema: param?.resource?.schema,
      kafka: param?.resource?.kafka
    } as Resource
  }
}

const canDescribeRoleBinding = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v2: queryV2ItemFn(CloudRolebindingsDescribe, { level: 'organization' })
    },
    debug
  )
}

const canAlterRoleBinding = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v2: queryV2ItemFn(CloudRolebindingsAlter, { level: 'organization' })
    },
    debug
  )
}

const canCreateRoleBinding = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v2: queryV2ItemFn(CloudRolebindingsCreate, { level: 'organization' })
    },
    debug
  )
}

const canRemoveRoleBinding = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v2: queryV2ItemFn(CloudRolebindingsDelete, { level: 'organization' })
    },
    debug
  )
}

const canAlterSelfOrganization = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v2: queryV2ItemFn(CloudSelfOrganizationsAlter, { level: 'organization' })
    },
    debug
  )
}

const canDescribeBilling = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        AND: [
          { action: view, subject: Product },
          { action: view, subject: Subscription }
        ]
      },
      v2: {
        AND: [
          queryV2ItemFn(BillingProductsDescribe, {
            level: 'organization'
          }),
          queryV2ItemFn(BillingSubscriptionsDescribe, {
            level: 'organization'
          })
        ]
      }
    },
    debug
  )
}

const canCreateBilling = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        AND: [
          { action: create, subject: PaymentIntent },
          { action: create, subject: CustomerPortalRequest }
        ]
      },
      v2: {
        AND: [
          queryV2ItemFn(BillingPaymentintentsCreate, {
            level: 'organization'
          }),
          queryV2ItemFn(BillingCustomerportalrequestsCreate, {
            level: 'organization'
          })
        ]
      }
    },
    debug
  )
}

const canDescribePaymentIntent = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        AND: [{ action: view, subject: PaymentIntent }]
      },
      v2: {
        AND: [
          queryV2ItemFn(BillingPaymentintentsDescribe, {
            level: 'organization'
          })
        ]
      }
    },
    debug
  )
}

const canCreatePaymentIntent = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        AND: [{ action: create, subject: PaymentIntent }]
      },
      v2: {
        AND: [
          queryV2ItemFn(BillingPaymentintentsCreate, {
            level: 'organization'
          })
        ]
      }
    },
    debug
  )
}

const canDescribePoolOptionList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v2: {
        AND: [
          queryV2ItemFn(CloudPoolsDescribe, {
            level: 'organization'
          }),
          queryV2ItemFn(CloudPooloptionsDescribe, {
            level: 'organization'
          })
        ]
      },
      v1: { action: view, subject: PoolOption }
    },
    debug
  )
}

const canDescribeSecretList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: view, subject: Secret },
      v2: queryV2ItemFn(CloudSecretsDescribe, {
        level: 'organization'
      })
    },
    debug
  )
}

const canDescribeSecret = (params: RbacStateParams = {}) => {
  return canDescribeSecretList(params)
}

const canCreateSecret = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: create, subject: Secret },
      v2: queryV2ItemFn(CloudSecretsCreate, {
        level: 'organization'
      })
    },
    debug
  )
}

const canUpdateSecret = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: Secret },
      v2: queryV2ItemFn(CloudSecretsAlter, { level: 'organization' })
    },
    debug
  )
}

const canRemoveSecret = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: remove, subject: Secret },
      v2: queryV2ItemFn(CloudSecretsDelete, { level: 'organization' })
    },
    debug
  )
}

const canDescribeUserList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: view, subject: User },
      v2: queryV2ItemFn(CloudUsersDescribe, { level: 'organization' })
    },
    debug
  )
}

const canDescribeUser = (params: RbacStateParams = {}) => {
  return canDescribeUserList(params)
}

const canCreateUser = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: create,
        subject: User
      },
      v2: queryV2ItemFn(CloudUsersCreate, { level: 'organization' })
    },
    debug
  )
}

const canUpdateUser = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: update,
        subject: User
      },
      v2: queryV2ItemFn(CloudUsersAlter, { level: 'organization' })
    },
    debug
  )
}

const canRemoveUser = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: remove,
        subject: User
      },
      v2: queryV2ItemFn(CloudUsersDelete, { level: 'organization' })
    },
    debug
  )
}

const canDescribeSAList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: view, subject: ServiceAccount },
      v2: queryV2ItemFn(CloudServiceaccountsDescribe, { level: 'organization' })
    },
    debug
  )
}

const canDescribeSA = ({ debug, serviceAccount }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: view, subject: ServiceAccount },
      v2: queryV2ItemFn(CloudServiceaccountsDescribe, {
        level: 'organization',
        resource: serviceAccount
          ? { cloud: { service_account: { name: serviceAccount } } }
          : undefined
      })
    },
    debug
  )
}

const canCreateSA = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: create,
        subject: ServiceAccount
      },
      v2: queryV2ItemFn(CloudServiceaccountsCreate, { level: 'organization' })
    },
    debug
  )
}

const canUpdateSA = ({ serviceAccount, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: update,
        subject: ServiceAccount
      },
      v2: queryV2ItemFn(CloudServiceaccountsAlter, {
        level: 'organization',
        resource: serviceAccount
          ? { cloud: { service_account: { name: serviceAccount } } }
          : undefined
      })
    },
    debug
  )
}

const canRemoveSA = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: remove,
        subject: ServiceAccount
      },
      v2: queryV2ItemFn(CloudServiceaccountsDelete, { level: 'organization' })
    },
    debug
  )
}

const canDescribeSABindingList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: view,
        subject: ServiceAccountBinding
      },
      v2: queryV2ItemFn(CloudServiceaccountbindingsDescribe, { level: 'organization' })
    },
    debug
  )
}

const canDescribeSABinding = (params: RbacStateParams = {}) => {
  return canDescribeSABindingList(params)
}

const canCreateSABinding = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: create,
        subject: ServiceAccountBinding
      },
      v2: queryV2ItemFn(CloudServiceaccountbindingsCreate, { level: 'organization' })
    },
    debug
  )
}

const canUpdateSABinding = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: ServiceAccountBinding },
      v2: queryV2ItemFn(CloudServiceaccountbindingsAlter, { level: 'organization' })
    },
    debug
  )
}

const canRemoveSABinding = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: remove,
        subject: ServiceAccountBinding
      },
      v2: queryV2ItemFn(CloudServiceaccountbindingsDelete, { level: 'organization' })
    },
    debug
  )
}

const canDescribeApiKeyList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: view, subject: APIKeys },
      v2: queryV2ItemFn(CloudApikeysDescribe, { level: 'organization' })
    },
    debug
  )
}

const canDescribeApiKey = (params: RbacStateParams = {}) => {
  return canDescribeApiKeyList(params)
}

const canCreateAPIKey = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: create,
        subject: APIKeys
      },
      v2: queryV2ItemFn(CloudApikeysCreate, { level: 'organization' })
    },
    debug
  )
}

const canUpdateAPIKey = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: update,
        subject: APIKeys
      },
      v2: queryV2ItemFn(CloudApikeysAlter, { level: 'organization' })
    },
    debug
  )
}

const canRemoveAPIKey = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        action: remove,
        subject: APIKeys
      },
      v2: queryV2ItemFn(CloudApikeysDelete, { level: 'organization' })
    },
    debug
  )
}

const canDescribeInstanceList = ({ debug, instanceType }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: view, subject: PulsarInstance },
          { action: browse, subject: PulsarInstance }
        ]
      },
      v2: queryV2ItemFn(
        instanceType === 'kafka' ? CloudInstancesDescribe : CloudPulsarinstancesDescribe,
        { level: 'organization' }
      )
    },
    debug
  )
}

const canDescribeInstance = ({
  instance: _instance,
  instanceType,
  debug
}: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: view, subject: PulsarInstance },
          { action: browse, subject: PulsarInstance }
        ]
      },
      v2: queryV2ItemFn(
        instanceType === 'kafka' ? CloudInstancesDescribe : CloudPulsarinstancesDescribe,
        {
          level: 'instance',
          resource: _instance ? { common: { instance: _instance } } : undefined
        }
      )
    },
    debug
  )
}

const canCreateInstance = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: create, subject: PulsarInstance },
      v2: {
        OR: [
          queryV2ItemFn(CloudPulsarinstancesCreate, { level: 'organization' }),
          queryV2ItemFn(CloudInstancesCreate, { level: 'organization' })
        ]
      }
    },
    debug
  )
}

const canUpdateInstance = ({ instance, instanceType, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: PulsarInstance },
      v2: queryV2ItemFn(
        instanceType === 'kafka' ? CloudInstancesAlter : CloudPulsarinstancesAlter,
        {
          level: 'instance',
          resource: instance ? { common: { instance } } : undefined
        }
      )
    },
    debug
  )
}

const canRemoveInstance = ({ instance, instanceType, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: remove, subject: PulsarInstance },
      v2: queryV2ItemFn(
        instanceType === 'kafka' ? CloudInstancesDelete : CloudPulsarinstancesDelete,
        {
          level: 'instance',
          resource: instance ? { common: { instance } } : undefined
        }
      )
    },
    debug
  )
}

const canDescribeClusterList = ({ instance, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: view, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(CloudPulsarclustersDescribe, {
        level: 'instance',
        resource: instance ? { common: { instance } } : undefined
      })
    },
    debug
  )
}

const canDescribeCluster = ({ cluster, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: view, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(CloudPulsarclustersDescribe, {
        level: 'cluster',
        resource: cluster ? { common: { cluster } } : undefined
      })
    },
    debug
  )
}

const canCreateCluster = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: create, subject: PulsarCluster },
      v2: queryV2ItemFn(CloudPulsarclustersCreate, {
        level: 'instance'
      })
    },
    debug
  )
}

const canUpdateCluster = ({ cluster, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2: queryV2ItemFn(CloudPulsarclustersAlter, {
        level: 'cluster',
        resource: cluster ? { common: { cluster } } : undefined
      })
    },
    debug
  )
}

const canReadBrokerConfiguration = () =>
  can({
    v1: { action: browse, subject: PulsarCluster },
    v2: queryV2ItemFn(PulsarBrokersDescribe, { level: 'cluster' })
  })

const canEditBrokerConfiguration = () =>
  can({
    v1: { action: update, subject: PulsarCluster },
    v2: queryV2ItemFn(PulsarBrokersAlter, { level: 'cluster' })
  })

const canRemoveCluster = ({ cluster, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: remove, subject: PulsarCluster },
      v2: queryV2ItemFn(CloudPulsarclustersDelete, {
        level: 'cluster',
        resource: cluster ? { common: { cluster } } : undefined
      })
    },
    debug
  )
}

const canDescribeKafkaClusterList = ({ instance, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: view, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(CloudKafkaClustersDescribe, {
        level: 'instance',
        resource: instance ? { common: { instance } } : undefined
      })
    },
    debug
  )
}

const canDescribeKafkaCluster = ({ cluster, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: view, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(CloudKafkaClustersDescribe, {
        level: 'cluster',
        resource: cluster ? { common: { cluster } } : undefined
      })
    },
    debug
  )
}

const canCreateKafkaCluster = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: create, subject: PulsarCluster },
      v2: queryV2ItemFn(CloudKafkaClustersCreate, {
        level: 'instance'
      })
    },
    debug
  )
}

const canUpdateKafkaCluster = ({ cluster, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2: queryV2ItemFn(CloudKafkaClustersAlter, {
        level: 'cluster',
        resource: cluster ? { common: { cluster } } : undefined
      })
    },
    debug
  )
}

const canRemoveKafkaCluster = ({ cluster, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: remove, subject: PulsarCluster },
      v2: queryV2ItemFn(CloudKafkaClustersDelete, {
        level: 'cluster',
        resource: cluster ? { common: { cluster } } : undefined
      })
    },
    debug
  )
}

const canDescribePulsarMetrics = ({ cluster, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: view, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(CloudMetricsPulsarQuery, {
        level: 'cluster',
        resource: cluster ? { common: { cluster } } : undefined
      })
    },
    debug
  )
}

const canDescribeTenantList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarTenantsDescribe, { level: 'cluster' })
    },
    debug
  )
}

const canDescribeTenant = ({ tenant, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarTenantsDescribe, {
        level: 'tenant',
        resource: tenant ? { common: { tenant } } : undefined
      })
    },
    debug
  )
}

const canCreateTenant = ({ debug }: RbacStateParams = {}) => {
  const { instance } = usePulsarState()
  const { activeClusterName } = useCluster()
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2: queryV2ItemFn(PulsarTenantsCreate, { level: 'cluster' })
      },
      debug
    ) ||
    can(
      {
        v1: {
          AND: [
            {
              action: adminTenant,
              subject: new PulsarInstanceWrapper({ metadata: { name: instance.value as string } })
            },
            {
              action: adminTenant,
              subject: new PulsarClusterWrapper({
                metadata: { name: activeClusterName.value as string }
              })
            }
          ]
        },
        v2: queryV2ItemFn(PulsarTenantsCreate, { level: 'cluster' })
      },
      debug
    )
  )
}

const canUpdateTenant = ({ tenant: _tenant, debug }: RbacStateParams = {}) => {
  const { tenant, instance } = usePulsarState()
  const { activeClusterName } = useCluster()
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2: queryV2ItemFn(PulsarTenantsAlter, {
          level: 'tenant',
          resource: _tenant ? { common: { tenant: _tenant } } : undefined
        })
      },
      debug
    ) ||
    can({
      v1: {
        action: adminTenant,
        subject: new TenantWrapper(
          _tenant || (tenant.value as string),
          instance.value as string,
          activeClusterName.value as string
        )
      },
      v2: queryV2ItemFn(PulsarTenantsAlter, {
        level: 'tenant',
        resource: _tenant ? { common: { tenant: _tenant } } : undefined
      })
    })
  )
}

const canRemoveTenant = ({ tenant: _tenant, debug }: RbacStateParams = {}) => {
  const { tenant, instance } = usePulsarState()
  const { activeClusterName } = useCluster()
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2: queryV2ItemFn(PulsarTenantsDelete, {
          level: 'tenant',
          resource: _tenant ? { common: { tenant: _tenant } } : undefined
        })
      },
      debug
    ) ||
    can({
      v1: {
        action: adminTenant,
        subject: new TenantWrapper(
          _tenant || (tenant.value as string),
          instance.value as string,
          activeClusterName.value as string
        )
      },
      v2: queryV2ItemFn(PulsarTenantsDelete, {
        level: 'tenant',
        resource: _tenant ? { common: { tenant: _tenant } } : undefined
      })
    })
  )
}

const canDescribeNamespaceList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarNamespacesDescribe, { level: 'tenant' })
    },
    debug
  )
}

const canDescribeNamespace = ({ namespace, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarNamespacesDescribe, {
        level: 'namespace',
        resource: namespace ? { common: { namespace } } : undefined
      })
    },
    debug
  )
}

const canCreateNamespace = ({ tenant: _tenant, debug }: RbacStateParams = {}) => {
  const { tenant, instance } = usePulsarState()
  const { activeClusterName } = useCluster()
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2: queryV2ItemFn(PulsarNamespacesCreate, { level: 'tenant' })
      },
      debug
    ) ||
    can(
      {
        v1: {
          action: adminTenant,
          subject: new TenantWrapper(
            _tenant || (tenant.value as string),
            instance.value as string,
            activeClusterName.value as string
          )
        },
        v2: queryV2ItemFn(PulsarNamespacesCreate, { level: 'tenant' })
      },
      debug
    )
  )
}

const canUpdateNamespace = ({ namespace, debug }: RbacStateParams = {}) => {
  const { tenant, instance } = usePulsarState()
  const { activeClusterName } = useCluster()
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2: queryV2ItemFn(PulsarNamespacesAlter, {
          level: 'namespace',
          resource: namespace ? { common: { namespace } } : undefined
        })
      },
      debug
    ) ||
    can({
      v1: {
        action: adminTenant,
        subject: new TenantWrapper(
          tenant.value as string,
          instance.value as string,
          activeClusterName.value as string
        )
      },
      v2: queryV2ItemFn(PulsarNamespacesAlter, {
        level: 'namespace',
        resource: namespace ? { common: { namespace } } : undefined
      })
    })
  )
}

const canRemoveNamespace = ({ namespace, debug }: RbacStateParams = {}) => {
  const { tenant, instance } = usePulsarState()
  const { activeClusterName } = useCluster()
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2: queryV2ItemFn(PulsarNamespacesDelete, {
          level: 'namespace',
          resource: namespace ? { common: { namespace } } : undefined
        })
      },
      debug
    ) ||
    can({
      v1: {
        action: adminTenant,
        subject: new TenantWrapper(
          tenant.value as string,
          instance.value as string,
          activeClusterName.value as string
        )
      },
      v2: queryV2ItemFn(PulsarNamespacesDelete, {
        level: 'namespace',
        resource: namespace ? { common: { namespace } } : undefined
      })
    })
  )
}

const canDescribeTopicList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarTopicsDescribe, { level: 'namespace' })
    },
    debug
  )
}

const canDescribeTopic = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarTopicsDescribe, {
        level: 'topic',
        resource: {
          common: { topic: topic_name },
          pulsar: {
            topic: {
              domain: topic_domain
            }
          }
        }
      })
    },
    debug
  )
}

const canCreateTopic = ({
  tenant: _tenant,
  namespace: _namespace,
  debug
}: RbacStateParams = {}) => {
  const { tenant, instance, namespace } = usePulsarState()
  const { activeClusterName } = useCluster()
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2: queryV2ItemFn(PulsarTopicsCreate, {
          level: 'namespace',
          resource: {
            common: {
              tenant: _tenant || (tenant.value as string),
              namespace: _namespace || (namespace.value as string)
            }
          }
        })
      },
      debug
    ) ||
    can(
      {
        v1: {
          action: adminTenant,
          subject: new TenantWrapper(
            _tenant || (tenant.value as string),
            instance.value as string,
            activeClusterName.value as string
          )
        },
        v2: queryV2ItemFn(PulsarTopicsCreate, {
          level: 'namespace',
          resource: {
            common: {
              tenant: _tenant || (tenant.value as string),
              namespace: _namespace || (namespace.value as string)
            }
          }
        })
      },
      debug
    )
  )
}

const canUpdateTopic = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  const { tenant, instance } = usePulsarState()
  const { activeClusterName } = useCluster()
  const v2 = queryV2ItemFn(PulsarTopicsAlter, {
    level: 'topic',
    resource: {
      common: { topic: topic_name },
      pulsar: {
        topic: {
          domain: topic_domain
        }
      }
    }
  })
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2
      },
      debug
    ) ||
    can(
      {
        v1: {
          action: adminTenant,
          subject: new TenantWrapper(
            tenant.value as string,
            instance.value as string,
            activeClusterName.value as string
          )
        },
        v2
      },
      debug
    )
  )
}

const canRemoveTopic = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  const { tenant, instance } = usePulsarState()
  const { activeClusterName } = useCluster()
  const v2 = queryV2ItemFn(PulsarTopicsDelete, {
    level: 'topic',
    resource: {
      common: { topic: topic_name },
      pulsar: {
        topic: {
          domain: topic_domain
        }
      }
    }
  })
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2
      },
      debug
    ) ||
    can(
      {
        v1: {
          action: adminTenant,
          subject: new TenantWrapper(
            tenant.value as string,
            instance.value as string,
            activeClusterName.value as string
          )
        },
        v2
      },
      debug
    )
  )
}

const canDescribeKafkaTopicList = ({ debug }: RbacStateParams = {}) => {
  return canDescribeTopicList({ debug })
}

const canDescribeKafkaTopic = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  return canDescribeTopic({ topic_domain, topic_name, debug })
}

const canCreateKafkaTopic = ({
  tenant: _tenant,
  namespace: _namespace,
  debug
}: RbacStateParams = {}) => {
  return canCreateTopic({ tenant: _tenant, namespace: _namespace, debug })
}

const canUpdateKafkaTopic = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  return canUpdateTopic({ topic_domain, topic_name, debug })
}

const canRemoveKafkaTopic = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  return canRemoveTopic({ topic_domain, topic_name, debug })
}

const canProduceMessages = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarMessagesProduce, {
        level: 'topic',
        resource: {
          common: { topic: topic_name },
          pulsar: {
            topic: {
              domain: topic_domain
            }
          }
        }
      })
    },
    debug
  )
}

const canConsumeMessages = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarMessagesConsume, {
        level: 'topic',
        resource: {
          common: { topic: topic_name },
          pulsar: {
            topic: {
              domain: topic_domain
            }
          }
        }
      })
    },
    debug
  )
}

const canProduceKafkaMessages = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  return canProduceMessages({ topic_domain, topic_name, debug })
}

const canConsumeKafkaMessages = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  return canConsumeMessages({ topic_domain, topic_name, debug })
}

const canDescribeTopicBroker = ({ topic_domain, topic_name, debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarBrokersDescribe, {
        level: 'topic',
        resource: {
          common: { topic: topic_name },
          pulsar: {
            topic: {
              domain: topic_domain
            }
          }
        }
      })
    },
    debug
  )
}

const canDescribeTopicSubscriptionList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarSubscriptionsDescribe, { level: 'topic' })
    },
    debug
  )
}

const canDescribeTopicSubscription = ({
  topic_domain,
  topic_name,
  subscription,
  debug
}: RbacStateParams = {}) => {
  return can(
    {
      v1: {
        OR: [
          { action: adminTenant, subject: PulsarCluster },
          { action: use, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2: queryV2ItemFn(PulsarSubscriptionsDescribe, {
        level: 'topic',
        resource: {
          common: { topic: topic_name },
          pulsar: {
            topic: {
              domain: topic_domain
            },
            subscription: {
              name: subscription
            }
          }
        }
      })
    },
    debug
  )
}

const canCreateTopicSubscription = ({
  tenant: _tenant,
  namespace: _namespace,
  topic_domain,
  topic_name,
  debug
}: RbacStateParams = {}) => {
  const { tenant, instance, namespace } = usePulsarState()
  const { activeClusterName } = useCluster()
  const v2 = queryV2ItemFn(PulsarSubscriptionsCreate, {
    level: 'topic',
    resource: {
      common: {
        tenant: _tenant || (tenant.value as string),
        namespace: _namespace || (namespace.value as string),
        topic: topic_name
      },
      pulsar: {
        topic: {
          domain: topic_domain
        }
      }
    }
  })
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2
      },
      debug
    ) ||
    can(
      {
        v1: {
          action: adminTenant,
          subject: new TenantWrapper(
            _tenant || (tenant.value as string),
            instance.value as string,
            activeClusterName.value as string
          )
        },
        v2
      },
      debug
    )
  )
}

const canUpdateTopicSubscription = ({
  topic_domain,
  topic_name,
  subscription,
  debug
}: RbacStateParams = {}) => {
  const { tenant, instance } = usePulsarState()
  const { activeClusterName } = useCluster()
  const v2 = queryV2ItemFn(PulsarSubscriptionsAlter, {
    level: 'topic',
    resource: {
      common: {
        topic: topic_name
      },
      pulsar: {
        topic: {
          domain: topic_domain
        },
        subscription: {
          name: subscription
        }
      }
    }
  })
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2
      },
      debug
    ) ||
    can(
      {
        v1: {
          action: adminTenant,
          subject: new TenantWrapper(
            tenant.value as string,
            instance.value as string,
            activeClusterName.value as string
          )
        },
        v2
      },
      debug
    )
  )
}

const canRemoveTopicSubscription = ({
  topic_domain,
  topic_name,
  subscription,
  debug
}: RbacStateParams = {}) => {
  const { tenant, instance } = usePulsarState()
  const { activeClusterName } = useCluster()
  const v2 = queryV2ItemFn(PulsarSubscriptionsDelete, {
    level: 'topic',
    resource: {
      common: { topic: topic_name },
      pulsar: {
        topic: {
          domain: topic_domain
        },
        subscription: {
          name: subscription
        }
      }
    }
  })
  return (
    can(
      {
        v1: { action: use, subject: PulsarCluster },
        v2
      },
      debug
    ) ||
    can(
      {
        v1: {
          action: adminTenant,
          subject: new TenantWrapper(
            tenant.value as string,
            instance.value as string,
            activeClusterName.value as string
          )
        },
        v2
      },
      debug
    )
  )
}

const canDescribeConnectorList = ({ to, connectorType, debug }: RbacStateParams = {}) => {
  let v2: RbacQueryV2 = {
    OR: [
      queryV2ItemFn(PulsarSinksDescribe, { level: 'cluster' }),
      queryV2ItemFn(PulsarSourcesDescribe, { level: 'cluster' })
    ]
  }
  if (to?.params?.type === 'sink' || connectorType === 'sink') {
    v2 = { AND: [queryV2ItemFn(PulsarSinksDescribe, { level: 'cluster' })] }
  } else if (to?.params?.type === 'source' || connectorType === 'source') {
    v2 = { AND: [queryV2ItemFn(PulsarSourcesDescribe, { level: 'cluster' })] }
  }
  return can(
    {
      v1: {
        OR: [
          { action: view, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2
    },
    debug
  )
}

const canDescribeConnector = (params: RbacStateParams = {}) => {
  return canDescribeConnectorList(params)
}

const canCreateConnector = ({ to, connectorType, debug }: RbacStateParams = {}) => {
  let v2: RbacQueryV2 = {
    OR: [
      queryV2ItemFn(PulsarSinksCreate, { level: 'cluster' }),
      queryV2ItemFn(PulsarSourcesCreate, { level: 'cluster' })
    ]
  }
  if (to?.params?.type === 'sinks' || connectorType === 'sink') {
    v2 = { AND: [queryV2ItemFn(PulsarSinksCreate, { level: 'cluster' })] }
  } else if (to?.params?.type === 'sources' || connectorType === 'source') {
    v2 = { AND: [queryV2ItemFn(PulsarSourcesCreate, { level: 'cluster' })] }
  }
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2
    },
    debug
  )
}

const canUpdateConnector = ({ to, connectorType, debug }: RbacStateParams = {}) => {
  let v2: RbacQueryV2 = {
    OR: [
      queryV2ItemFn(PulsarSinksAlter, { level: 'cluster' }),
      queryV2ItemFn(PulsarSourcesAlter, { level: 'cluster' })
    ]
  }
  if (to?.params?.type === 'sinks' || connectorType === 'sink') {
    v2 = { AND: [queryV2ItemFn(PulsarSinksAlter, { level: 'cluster' })] }
  } else if (to?.params?.type === 'sources' || connectorType === 'source') {
    v2 = { AND: [queryV2ItemFn(PulsarSourcesAlter, { level: 'cluster' })] }
  }
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2
    },
    debug
  )
}

const canRemoveConnector = ({ to, connectorType, debug }: RbacStateParams = {}) => {
  let v2: RbacQueryV2 = {
    OR: [
      queryV2ItemFn(PulsarSinksDelete, { level: 'cluster' }),
      queryV2ItemFn(PulsarSourcesDelete, { level: 'cluster' })
    ]
  }
  if (to?.params?.type === 'sinks' || connectorType === 'sink') {
    v2 = { AND: [queryV2ItemFn(PulsarSinksDelete, { level: 'cluster' })] }
  } else if (to?.params?.type === 'sources' || connectorType === 'source') {
    v2 = { AND: [queryV2ItemFn(PulsarSourcesDelete, { level: 'cluster' })] }
  }
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2
    },
    debug
  )
}

const canDescribeKsnConnectorList = ({ to, connectorType, debug }: RbacStateParams = {}) => {
  let v2: RbacQueryV2 = {
    OR: [
      queryV2ItemFn(KsnSinksDescribe, { level: 'cluster' }),
      queryV2ItemFn(KsnSourcesDescribe, { level: 'cluster' })
    ]
  }
  v2 = { AND: [queryV2ItemFn(CloudPulsarclustersDescribe, { level: 'cluster' })] }
  // TODO: open these code after the RBAC for ksn is ready from the backend side
  // if (to?.params?.type === 'sink' || connectorType === 'sink') {
  //   v2 = { AND: [queryV2ItemFn(KsnSinksDescribe, { level: 'cluster' })] }
  // } else if (to?.params?.type === 'source' || connectorType === 'source') {
  //   v2 = { AND: [queryV2ItemFn(KsnSourcesDescribe, { level: 'cluster' })] }
  // }
  return can(
    {
      v1: {
        OR: [
          { action: view, subject: PulsarCluster },
          { action: browse, subject: PulsarCluster }
        ]
      },
      v2
    },
    debug
  )
}

const canDescribeKsnConnector = (params: RbacStateParams = {}) => {
  return canDescribeKsnConnectorList(params)
}

const canCreateKsnConnector = ({ to, connectorType, debug }: RbacStateParams = {}) => {
  let v2: RbacQueryV2 = {
    OR: [
      queryV2ItemFn(KsnSinksCreate, { level: 'cluster' }),
      queryV2ItemFn(KsnSourcesCreate, { level: 'cluster' })
    ]
  }
  v2 = { AND: [queryV2ItemFn(CloudPulsarclustersAlter, { level: 'cluster' })] }
  // TODO: open these code after the RBAC for ksn is ready from the backend side
  // if (to?.params?.type === 'sinks' || connectorType === 'sink') {
  //   v2 = { AND: [queryV2ItemFn(KsnSinksCreate, { level: 'cluster' })] }
  // } else if (to?.params?.type === 'sources' || connectorType === 'source') {
  //   v2 = { AND: [queryV2ItemFn(KsnSourcesCreate, { level: 'cluster' })] }
  // }
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2
    },
    debug
  )
}

const canUpdateKsnConnector = ({ to, connectorType, debug }: RbacStateParams = {}) => {
  let v2: RbacQueryV2 = {
    OR: [
      queryV2ItemFn(KsnSinksAlter, { level: 'cluster' }),
      queryV2ItemFn(KsnSourcesAlter, { level: 'cluster' })
    ]
  }
  v2 = { AND: [queryV2ItemFn(CloudPulsarclustersAlter, { level: 'cluster' })] }
  // TODO: open these code after the RBAC for ksn is ready from the backend side
  // if (to?.params?.type === 'sinks' || connectorType === 'sink') {
  //   v2 = { AND: [queryV2ItemFn(KsnSinksAlter, { level: 'cluster' })] }
  // } else if (to?.params?.type === 'sources' || connectorType === 'source') {
  //   v2 = { AND: [queryV2ItemFn(KsnSourcesAlter, { level: 'cluster' })] }
  // }
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2
    },
    debug
  )
}

const canRemoveKsnConnector = ({ to, connectorType, debug }: RbacStateParams = {}) => {
  let v2: RbacQueryV2 = {
    OR: [
      queryV2ItemFn(KsnSourcesDelete, { level: 'cluster' }),
      queryV2ItemFn(KsnSinksDelete, { level: 'cluster' })
    ]
  }
  v2 = { AND: [queryV2ItemFn(CloudPulsarclustersAlter, { level: 'cluster' })] }
  // TODO: open these code after the RBAC for ksn is ready from the backend side
  // if (to?.params?.type === 'sinks' || connectorType === 'sink') {
  //   v2 = { AND: [queryV2ItemFn(KsnSourcesDelete, { level: 'cluster' })] }
  // } else if (to?.params?.type === 'sources' || connectorType === 'source') {
  //   v2 = { AND: [queryV2ItemFn(KsnSourcesDelete, { level: 'cluster' })] }
  // }
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2
    },
    debug
  )
}

const canDescribeFunctionList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: view, subject: PulsarCluster },
      v2: queryV2ItemFn(PulsarFunctionsDescribe, { level: 'cluster' })
    },
    debug
  )
}

const canDescribeFunction = (params: RbacStateParams = {}) => {
  return canDescribeFunctionList(params)
}

const canCreateFunction = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2: queryV2ItemFn(PulsarFunctionsCreate, { level: 'cluster' })
    },
    debug
  )
}

const canUpdateFunction = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2: queryV2ItemFn(PulsarFunctionsAlter, { level: 'cluster' })
    },
    debug
  )
}

const canRemoveFunction = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: PulsarCluster },
      v2: queryV2ItemFn(PulsarFunctionsDelete, { level: 'cluster' })
    },
    debug
  )
}

const canDescribePfSqlList = (params: RbacStateParams = {}) => {
  return canDescribeFunctionList(params)
}

const canDescribePfSql = (params: RbacStateParams = {}) => {
  return canDescribeFunction(params)
}

const canCreatePfSql = (params: RbacStateParams = {}) => {
  return canCreateFunction(params)
}

const canUpdatePfSql = (params: RbacStateParams = {}) => {
  return canUpdateFunction(params)
}

const canRemovePfSql = (params: RbacStateParams = {}) => {
  return canRemoveFunction(params)
}

const canDescribeCloudEnvironmentList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: view, subject: CloudEnvironment },
      v2: queryV2ItemFn(CloudCloudenvironmentsDescribe, { level: 'organization' })
    },
    debug
  )
}

const canDescribeCloudEnvironment = (params: RbacStateParams = {}) => {
  return canDescribeCloudEnvironmentList(params)
}

const canCreateCloudEnvironment = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: create, subject: CloudEnvironment },
      v2: queryV2ItemFn(CloudCloudenvironmentsCreate, { level: 'organization' })
    },
    debug
  )
}

const canUpdateCloudEnvironment = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: CloudEnvironment },
      v2: queryV2ItemFn(CloudCloudenvironmentsAlter, { level: 'organization' })
    },
    debug
  )
}

const canRemoveCloudEnvironment = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: remove, subject: CloudEnvironment },
      v2: queryV2ItemFn(CloudCloudenvironmentsDelete, { level: 'organization' })
    },
    debug
  )
}

const canDescribeCloudConnectionList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: view, subject: CloudConnection },
      v2: queryV2ItemFn(CloudCloudconnectionsDescribe, { level: 'organization' })
    },
    debug
  )
}

const canDescribeCloudConnection = (params: RbacStateParams = {}) => {
  return canDescribeCloudConnectionList(params)
}

const canCreateCloudConnection = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: create, subject: CloudConnection },
      v2: queryV2ItemFn(CloudCloudconnectionsCreate, { level: 'organization' })
    },
    debug
  )
}

const canUpdateCloudConnection = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: CloudConnection },
      v2: queryV2ItemFn(CloudCloudconnectionsAlter, { level: 'organization' })
    },
    debug
  )
}

const canRemoveCloudConnection = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: remove, subject: CloudConnection },
      v2: queryV2ItemFn(CloudCloudconnectionsDelete, { level: 'organization' })
    },
    debug
  )
}

const canDescribeVolumeList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: view, subject: Volume },
      v2: queryV2ItemFn(CloudVolumesDescribe, { level: 'organization' })
    },
    debug
  )
}

const canDescribeCloudVolume = (params: RbacStateParams = {}) => {
  return canDescribeVolumeList(params)
}

const canCreateVolume = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: create, subject: Volume },
      v2: queryV2ItemFn(CloudVolumesCreate, { level: 'organization' })
    },
    debug
  )
}

const canUpdateVolume = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: Volume },
      v2: queryV2ItemFn(CloudVolumesAlter, { level: 'organization' })
    },
    debug
  )
}

const canRemoveVolume = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: remove, subject: Volume },
      v2: queryV2ItemFn(CloudVolumesDelete, { level: 'organization' })
    },
    debug
  )
}

const canDescribeCatalogList = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: view, subject: Catalog },
      v2: queryV2ItemFn(CloudCatalogsDescribe, { level: 'organization' })
    },
    debug
  )
}

const canDescribeCatalog = (params: RbacStateParams = {}) => {
  return canDescribeCatalogList(params)
}

const canCreateCatalog = ({ debug, catalog }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: create, subject: Catalog },
      v2: queryV2ItemFn(CloudCatalogsCreate, {
        level: 'organization',
        resource: { cloud: { catalog: { name: catalog } } }
      })
    },
    debug
  )
}

const canUpdateCatalog = ({ debug, catalog }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: Catalog },
      v2: queryV2ItemFn(CloudCatalogsAlter, {
        level: 'organization',
        resource: { cloud: { catalog: { name: catalog } } }
      })
    },
    debug
  )
}

const canRemoveCatalog = ({ debug, catalog }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: remove, subject: Catalog },
      v2: queryV2ItemFn(CloudCatalogsDelete, {
        level: 'organization',
        resource: { cloud: { catalog: { name: catalog } } }
      })
    },
    debug
  )
}

const canDescribeUnilinkList = ({ debug }: RbacStateParams = {}) => {
  return can({
    v1: { action: view, subject: Unilink },
    v2: queryV2ItemFn(CloudUnilinkDescribe, { level: 'organization' })
  })
}

const canDescribeUnilink = (params: RbacStateParams = {}) => {
  return canDescribeUnilinkList(params)
}

const canCreateUnilink = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: create, subject: Unilink },
      v2: queryV2ItemFn(CloudUnilinkCreate, { level: 'organization' })
    },
    debug
  )
}

const canUpdateUnilink = ({ debug }: RbacStateParams = {}) => {
  return can(
    {
      v1: { action: update, subject: Unilink },
      v2: queryV2ItemFn(CloudUnilinkAlter, { level: 'organization' })
    },
    debug
  )
}

export const canRemoveUnilink = ({ debug }: RbacStateParams = {}) => {
  return can({
    v1: { action: remove, subject: Unilink },
    v2: queryV2ItemFn(CloudUnilinkDelete, { level: 'organization' })
  })
}

const canDescribeAlertList = ({ debug }: RbacStateParams = {}) => {
  return can({
    v2: queryV2ItemFn(CloudAlertsDescribe, { level: 'organization' })
  })
}

const canDescribeAlert = (params: RbacStateParams = {}) => {
  return canDescribeAlertList(params)
}

const canCreateAlert = ({ debug }: RbacStateParams = {}) => {
  return can({
    v2: queryV2ItemFn(CloudAlertsCreate, { level: 'organization' })
  })
}

const canUpdateAlert = ({ debug }: RbacStateParams = {}) => {
  return can({
    v2: queryV2ItemFn(CloudAlertsAlter, { level: 'organization' })
  })
}

const canRemoveAlert = ({ debug }: RbacStateParams = {}) => {
  return can({
    v2: queryV2ItemFn(CloudAlertsDelete, { level: 'organization' })
  })
}

// ================= Kafka Schema Permissions =================
// Read subjects list and subject details
const canDescribeSchemaSubjects = ({ debug }: RbacStateParams = {}) => {
  return can({ v2: queryV2ItemFn(SchemaSubjectsRead, { level: 'cluster' }) }, debug)
}

// Create or update schema subject (register new version)
const canUpsertSchemaSubject = ({ debug }: RbacStateParams = {}) => {
  return can({ v2: queryV2ItemFn(SchemaSubjectsWrite, { level: 'cluster' }) }, debug)
}

// Delete subject (soft/permanent)
const canRemoveSchemaSubject = ({ debug, schema_subject }: RbacStateParams = {}) => {
  return can(
    {
      v2: queryV2ItemFn(SchemaSubjectsDelete, {
        level: 'cluster',
        resource: { schema: { subject: schema_subject } }
      })
    },
    debug
  )
}

// Read compatibility config
const canDescribeSchemaCompatibility = ({ debug, schema_subject }: RbacStateParams = {}) => {
  return can(
    {
      v2: queryV2ItemFn(SchemaCompatibilitiesRead, {
        level: 'cluster',
        resource: { schema: { subject: schema_subject } }
      })
    },
    debug
  )
}

// Update compatibility config
const canUpdateSchemaCompatibility = ({ debug, schema_subject }: RbacStateParams = {}) => {
  return can(
    {
      v2: queryV2ItemFn(SchemaCompatibilitiesWrite, {
        level: 'cluster',
        resource: { schema: { subject: schema_subject } }
      })
    },
    debug
  )
}

export const rbacManager = () => {
  return {
    queryV2ItemFn,

    canDescribeRoleBinding,
    canAlterRoleBinding,
    canCreateRoleBinding,
    canRemoveRoleBinding,

    canAlterSelfOrganization,

    canDescribeBilling,
    canCreateBilling,
    canDescribePaymentIntent,

    canDescribePoolOptionList,

    canDescribeSecretList,
    canDescribeSecret,
    canCreateSecret,
    canUpdateSecret,
    canRemoveSecret,

    canDescribeUserList,
    canDescribeUser,
    canCreateUser,
    canRemoveUser,
    canUpdateUser,

    canDescribeSAList,
    canDescribeSA,
    canCreateSA,
    canUpdateSA,
    canRemoveSA,

    canDescribeSABindingList,
    canDescribeSABinding,
    canCreateSABinding,
    canUpdateSABinding,
    canRemoveSABinding,

    canDescribeApiKeyList,
    canDescribeApiKey,
    canCreateAPIKey,
    canUpdateAPIKey,
    canRemoveAPIKey,

    canDescribeInstanceList,
    canDescribeInstance,
    canCreateInstance,
    canUpdateInstance,
    canRemoveInstance,

    canDescribeClusterList,
    canDescribeCluster,
    canCreateCluster,
    canUpdateCluster,
    canReadBrokerConfiguration,
    canEditBrokerConfiguration,
    canRemoveCluster,

    canDescribeKafkaTopicList,
    canDescribeKafkaTopic,
    canCreateKafkaTopic,
    canUpdateKafkaTopic,
    canRemoveKafkaTopic,

    canDescribeKafkaClusterList,
    canDescribeKafkaCluster,
    canCreateKafkaCluster,
    canUpdateKafkaCluster,
    canRemoveKafkaCluster,

    canDescribePulsarMetrics,

    canDescribeTenantList,
    canDescribeTenant,
    canCreateTenant,
    canUpdateTenant,
    canRemoveTenant,

    canDescribeNamespaceList,
    canDescribeNamespace,
    canCreateNamespace,
    canUpdateNamespace,
    canRemoveNamespace,

    canDescribeTopicList,
    canDescribeTopic,
    canCreateTopic,
    canUpdateTopic,
    canRemoveTopic,

    canProduceMessages,
    canConsumeMessages,

    canProduceKafkaMessages,
    canConsumeKafkaMessages,

    canDescribeTopicBroker,

    canDescribeTopicSubscriptionList,
    canDescribeTopicSubscription,
    canCreateTopicSubscription,
    canUpdateTopicSubscription,
    canRemoveTopicSubscription,

    canDescribeConnectorList,
    canDescribeConnector,
    canCreateConnector,
    canUpdateConnector,
    canRemoveConnector,

    canDescribeKsnConnectorList,
    canDescribeKsnConnector,
    canCreateKsnConnector,
    canUpdateKsnConnector,
    canRemoveKsnConnector,

    canDescribeFunctionList,
    canDescribeFunction,
    canCreateFunction,
    canUpdateFunction,
    canRemoveFunction,

    canDescribePfSqlList,
    canDescribePfSql,
    canCreatePfSql,
    canUpdatePfSql,
    canRemovePfSql,

    canDescribeCloudEnvironment,
    canDescribeCloudEnvironmentList,
    canCreateCloudEnvironment,
    canUpdateCloudEnvironment,
    canRemoveCloudEnvironment,

    canDescribeCloudConnectionList,
    canDescribeCloudConnection,
    canCreateCloudConnection,
    canUpdateCloudConnection,
    canRemoveCloudConnection,

    canDescribeUnilinkList,
    canDescribeUnilink,
    canCreateUnilink,
    canUpdateUnilink,
    canRemoveUnilink,

    canDescribeVolumeList,
    canDescribeCloudVolume,
    canCreateVolume,
    canUpdateVolume,
    canRemoveVolume,

    canDescribeCatalogList,
    canDescribeCatalog,
    canCreateCatalog,
    canUpdateCatalog,
    canRemoveCatalog,

    canDescribeAlertList,
    canDescribeAlert,
    canCreateAlert,
    canUpdateAlert,
    canRemoveAlert,

    // Kafka Schema
    canDescribeSchemaSubjects,
    canUpsertSchemaSubject,
    canRemoveSchemaSubject,
    canDescribeSchemaCompatibility,
    canUpdateSchemaCompatibility
  }
}
